import { DownOutlined, WarningOutlined, CalendarOutlined } from '@ant-design/icons';
import { Row, Col, DatePicker, Checkbox } from 'antd';
import { optionLaptime } from 'constants/index';
import { medicalInstitution } from 'containers/App/selectors';
import { Formik, FormikHelpers } from 'formik';
import { Form, Input, Select, SubmitButton } from 'formik-antd';
import { useScrollTop } from 'hooks';
import moment from 'moment';
import { month } from 'pages/Register/RegisterFirstStep/data';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { ModalWarningLeavePage } from 'components';
import { t_cpap_instructions } from 'types';
import { date } from '../RegisterFirstStep/data';
import { newPatient } from '../slices';
import Wrap from './styles';

type Props = {
  nextStep: () => void;
};

type CountPrevMonth = {
  month?: string;
  year?: string;
  date?: string;
};

const RegisterSecondStep: FC<Props> = ({ nextStep }) => {
  useScrollTop();
  const { Option } = Select;
  const { TextArea } = Input;
  const dispatch = useDispatch();
  const downOutlined = <DownOutlined />;
  const { medical, cpapInstructions, dataStoreFields } = useSelector((state: RootState) => state.register);
  const medicalInstitutionList = useSelector(medicalInstitution);
  const [pickDate, setPickDate] = useState<string | undefined>(cpapInstructions.installation_date);
  const [countPrevMonth, setCountPrevMonth] = useState<CountPrevMonth>({
    month: cpapInstructions.last_month?.slice(4),
    year: cpapInstructions.last_month?.slice(0, 4),
  });
  const [accMask, setAccMask] = useState<string | undefined>(cpapInstructions.acc_mask);
  const [accMaskSize, setAccMaskSize] = useState<string | undefined>(cpapInstructions.acc_mask_size);
  const [accessoriesState, setAccessoriesState] = useState<string[]>(cpapInstructions.accessories || []);
  const [fieldBirthDay, setFieldBirthDay] = useState<string>();
  const [machine, setMachine] = useState<string | undefined>(cpapInstructions.machine);
  const [collection, setCollection] = useState<CountPrevMonth>({
    date: cpapInstructions.collection_date?.slice(2),
    month: cpapInstructions.collection_date?.slice(0, 2),
  });
  const [spPrice, setSpPrice] = useState<string>(cpapInstructions.sp_price || '');
  const [checked, setChecked] = useState<boolean>(!cpapInstructions.sp_price);
  const [exploitStop, setExploitStop] = useState<number>(cpapInstructions.suspension_flg || 0);
  const [checkedExploitStop, setCheckedExploitStop] = useState<boolean>(!cpapInstructions.suspension_flg);
  const medicalInfo = useMemo(() => {
    return medicalInstitutionList.find((f) => f.i_id === medical.id);
  }, [medicalInstitutionList, medical.id]);
  const selectAccessories = useMemo(() => {
    return dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'accessories')[1].options || [];
  }, [dataStoreFields]);

  const selectAccMax = useMemo(() => {
    return dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'acc_mask')[1].options || [];
  }, [dataStoreFields]);

  const selectMachines = useMemo(() => {
    return dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'machine')[1].options || [];
  }, [dataStoreFields]);

  const selectAccMaxSize = useMemo(() => {
    return (
      dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'acc_mask_size')[1].options || []
    );
  }, [dataStoreFields]);

  const displayGender = useMemo(() => {
    const options: any[] =
      dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'gender')[1].options || [];
    const gender = options.find((f) => f.option_id === cpapInstructions.gender);

    return gender ? gender.value : '';
  }, [dataStoreFields, cpapInstructions.gender]);

  function check(e: any) {
    const index = accessoriesState.findIndex((f) => f === e.target.value);
    if (index > -1) {
      const newArr = [...accessoriesState];
      newArr.splice(index, 1);
      setAccessoriesState(newArr);
    } else {
      setAccessoriesState((oldState) => [...oldState, e.target.value]);
    }
  }
  const checkAccessories = useCallback(
    (value: string): boolean => {
      return accessoriesState.findIndex((f: any) => f === value) > -1;
    },
    [accessoriesState],
  );

  const SelectCollectionDate = date(new Date().getDate(), collection.month ? +collection.month : 1);
  const optionMonths = month();

  useEffect(() => {
    function calculate_age(dob: any) {
      const diff_ms = Date.now() - dob.getTime();
      const age_dt = new Date(diff_ms);

      return Math.abs(age_dt.getUTCFullYear() - 1970);
    }
    if (cpapInstructions.patient_birthday) {
      const year = cpapInstructions.patient_birthday.slice(0, 4);
      const month = cpapInstructions.patient_birthday.slice(4, 6);
      const date = cpapInstructions.patient_birthday.slice(6, 8);

      const age = calculate_age(new Date(+year, +month, +date));

      setFieldBirthDay(`${year}年${month}月${date}日（${age}歳）`);
    }
  }, [cpapInstructions]);

  const valueMachine = selectMachines.find((s: any, key: any) => (s.display_id === machine ? s.value : ''));
  const valueAccessories = accessoriesState
    .map((acc) => {
      const dataFind = selectAccessories.find((s: any, key: any) => s.display_id === acc);
      return dataFind.value || '';
    })
    .join();
  const valueAccMax = selectAccMax.find((s: any, key: any) => (s.display_id === accMask ? s.value : ''));
  const valueAccMaxSize = selectAccMaxSize.find((s: any, key: any) => (s.display_id === accMaskSize ? s.value : ''));
  const dataDate = collection.month && collection.date ? `${+collection.month}月${+collection.date}日` : '';
  const lastMonth =
    countPrevMonth.year && countPrevMonth.month ? `${countPrevMonth.year}年${+countPrevMonth.month}月` : '';
  const installation_date = moment(pickDate).format('YYYY年M月D日');

  const handleSubmit = (values: t_cpap_instructions, form: FormikHelpers<t_cpap_instructions>) => {
    form.setSubmitting(false);

    const last_month =
      countPrevMonth.year && countPrevMonth.month ? `${countPrevMonth.year}${countPrevMonth.month}` : undefined;

    const setting: any = {
      sp_price: spPrice,
      clerk: values.clerk,
      transfer_history: values.transfer_history,
      lamp_time_max: values.lamp_time_max,
      lamp_time: values.lamp_time,
      pressure_max: values.pressure_max,
      pressure_min: values.pressure_min,
      accessories: accessoriesState,
      last_month: last_month,
      installation_date: pickDate,
      cpap_remark: values.cpap_remark,
      remark_for_output: values.remark_for_output,
      suspension_flg: exploitStop,
      machine: machine,
      machine_other: machine === 'o_other' ? values.machine_other : '',
      machine_no: values.machine_no,
      machine_epr_no: values.machine_epr_no,
      machine_dn_no: values.machine_dn_no,
      acc_mask: accMask,
      acc_mask_other: values.acc_mask_other,
      acc_mask_size: accMaskSize,
      acc_mask_size_other: values.acc_mask_size_other,
      collection_date: collection.month && collection.date ? `${collection.month}${collection.date}` : undefined,
      facility_name_for_search: `${medicalInfo?.facility_name} ${medicalInfo?.facility_address1} ${medicalInfo?.facility_address2}`,
      cpap_setting_for_search: `${displayGender} ${
        valueMachine ? valueMachine.value : ''
      } ${valueAccessories}加湿器、加温ホース ${valueAccMax ? valueAccMax.value : ''} ${
        valueAccMaxSize ? valueAccMaxSize.value : ''
      } ${spPrice === 'o_sp_price' ? '特別料金対象者' : ''} ${dataDate} ${lastMonth} ${installation_date}`,
      humidifier_machine_no: values.humidifier_machine_no,
    };
    dispatch(newPatient({ ...cpapInstructions, ...setting }));
    nextStep();
  };

  const onChangeYear = (date: any, dateString: string) => {
    setCountPrevMonth({ ...countPrevMonth, year: dateString });
  };

  const onChangePickDate = (date: any, dateString: string) => {
    setPickDate(dateString ? moment(dateString, 'YYYY年MM月DD日').format('YYYY-MM-DDThh:mm:ss') : dateString);
  };

  useEffect(() => {
    if (spPrice) {
      setChecked(true);
    } else {
      setChecked(false);
    }
  }, [spPrice]);

  const handleChange = () => {
    if (!spPrice) {
      setSpPrice('o_sp_price');
      setChecked(true);
    } else {
      setSpPrice('');
      setChecked(false);
    }
  };
  const handleChaneSuspen = () => {
    if (!checkedExploitStop) {
      setExploitStop(0);
      setCheckedExploitStop(true);
    } else {
      setExploitStop(1);
      setCheckedExploitStop(false);
    }
  };

  return (
    <Wrap>
      <div>
        <span className="standardFont ※">※</span>
        <span className="standardFont">は必須項目</span>
      </div>
      <Formik
        onSubmit={handleSubmit}
        initialValues={{}}
        render={({ isValid }) => (
          <Form autoComplete={`${Math.random()}`}>
            <div>
              <div className="title">
                <div className="rec" />
                <p className="largeBoldFont textTitle">医療機関</p>
              </div>
              <div className="form">
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">医療機関名</p>
                  </Col>
                  <Col span={19}>
                    <p className="middleBoldFont">{medicalInfo?.facility_name}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">所在地</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{`${
                      medicalInfo?.facility_zip ? `〒${medicalInfo?.facility_zip}` : ''
                    }  ${medicalInfo?.facility_address1 || ''}  ${medicalInfo?.facility_address2 || ''}`}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">TEL</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{medicalInfo?.facility_tel}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">営業担当</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{cpapInstructions.in_charge_of_sales}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <span className="standardBoldFont space">事務担当</span>
                  </Col>
                  <Col span={9}>
                    <Form.Item name="clerk">
                      <Input name="clerk" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <span className="standardBoldFont space">転院履歴</span>
                  </Col>
                  <Col span={19}>
                    <Form.Item name="transfer_history">
                      <TextArea name="transfer_history" rows={4} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
            <div>
              <div className="title">
                <div className="rec" />
                <p className="largeBoldFont textTitle">療養者</p>
              </div>
              <div className="form">
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">お名前</p>
                  </Col>
                  <Col span={19}>
                    <p className="middleBoldFont">{`${cpapInstructions.patient_name} (${cpapInstructions.patient_name_kana})`}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">患者ID</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{cpapInstructions.patient_id}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">性別</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{displayGender}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">生年月日</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{fieldBirthDay}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont spaceLine">設置住所</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">{`〒${cpapInstructions.zip_code}  ${cpapInstructions.address_1}  ${cpapInstructions.address_2}`}</p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5}>
                    <p className="standardBoldFont">TEL</p>
                  </Col>
                  <Col span={19}>
                    <p className="standardFont">
                      {cpapInstructions.phone_number ||
                        (cpapInstructions.mobile_phone_number && `【携帯】${cpapInstructions.mobile_phone_number}`)}
                      {cpapInstructions.phone_number && cpapInstructions.mobile_phone_number
                        ? `/ 【携帯】 ${cpapInstructions.mobile_phone_number} `
                        : ''}
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col span={5} className="standardBoldFont" style={{ marginTop: 5 }}>
                    特別料金対象者
                  </Col>
                  <Col span={19}>
                    <Form.Item name="sp_price">
                      <Checkbox value={spPrice} checked={checked} onChange={handleChange}>
                        対象
                      </Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
            <div>
              <div className="title">
                <div className="rec" />
                <p className="largeBoldFont textTitle">設 定</p>
              </div>
              <div className="form1">
                <Row>
                  <Col span={5} className="alignCenter mb-24">
                    <span className="standardBoldFont">基本設定</span>
                  </Col>
                  <Col span={19} className="d-flex">
                    <span className="space1 standardFont">圧力</span>
                    <Form.Item name="pressure_min">
                      <Input name="pressure_min" autoComplete={`${Math.random()}`} style={{ width: '60px' }} />
                    </Form.Item>
                    <span className="space1 standardFont">~</span>
                    <Form.Item name="pressure_max">
                      <Input name="pressure_max" autoComplete={`${Math.random()}`} style={{ width: '60px' }} />
                    </Form.Item>
                    <span className="space1 standardFont">cmH2O</span>
                    <div className="l space2" />
                    <span className="space1 standardFont">ランプ時間</span>
                    <Form.Item name="lamp_time">
                      <Select name="lamp_time" style={{ width: '70px' }}>
                        {optionLaptime.map((value) => (
                          <Select.Option value={value}>{value}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <span className="space1 standardFont">分</span>
                    <div className="l space2" />
                    <span className="space1 standardFont">最大ランプ時間</span>
                    <Form.Item name="lamp_time_max">
                      <Select name="lamp_time_max" style={{ width: '70px' }}>
                        {optionLaptime.map((value) => (
                          <Select.Option value={value}>{value}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <span className="space1 standardFont">分</span>
                  </Col>
                </Row>
              </div>
              <div className="form">
                <Row>
                  <Col span={5} className="alignCenter">
                    <span className="standardBoldFont">機種</span>
                  </Col>
                  <Col span={19}>
                    <Row className="bb-1" style={{ paddingBottom: '6px' }}>
                      <Col span={8}>
                        <Form.Item name="machine">
                          <Select
                            name="machine"
                            value={machine}
                            style={{ width: '100%' }}
                            onChange={(value) => {
                              setMachine(value);
                            }}
                          >
                            {selectMachines.map((s: any, i: number) => (
                              <Option key={i} value={s.display_id}>
                                {s.value}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name="machine_other" style={{ marginLeft: '6px' }}>
                          <Input
                            disabled={machine !== 'o_other'}
                            name="machine_other"
                            autoComplete={`${Math.random()}`}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={2} className="d-flex alignCenter" style={{ justifyContent: 'center' }}>
                        <span>機番</span>
                      </Col>
                      <Col span={10}>
                        <Form.Item name="machine_no">
                          <Input name="machine_no" autoComplete={`${Math.random()}`} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row className="bb-1">
                      <Col span={2} className="d-flex alignCenter">
                        <span>EPR</span>
                      </Col>
                      <Col span={4}>
                        <Form.Item name="machine_epr_no">
                          <Input name="machine_epr_no" maxLength={1} autoComplete={`${Math.random()}`} />
                        </Form.Item>
                      </Col>
                      <Col span={2} className="d-flex alignCenter" style={{ justifyContent: 'center' }}>
                        <span>DN/キー</span>
                      </Col>
                      <Col span={4}>
                        <Form.Item name="machine_dn_no">
                          <Input name="machine_dn_no" maxLength={4} autoComplete={`${Math.random()}`} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
              <div className="form">
                <Row>
                  <Col span={5} className="alignCenter">
                    <span className="standardBoldFont">付属品</span>
                  </Col>
                  <Col span={19}>
                    <Row className="bb-1 mb-6px">
                      <Col span={4} className="itemCenter">
                        <span>{selectAccessories[0].value}</span>
                      </Col>
                      <Col span={2} className="d-flex alignCenter itemCenter">
                        <span>種類</span>
                      </Col>
                      <Col span={4}>
                        <Select
                          name="acc_mask"
                          className="acc_mask"
                          value={accMask}
                          style={{ width: '100%' }}
                          onChange={(e) => setAccMask(e)}
                        >
                          {selectAccMax.map((s: any, i: number) => (
                            <Option key={i} value={s.display_id}>
                              {s.value}
                            </Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={3} style={{ marginLeft: '6px' }}>
                        <Form.Item className="mb-0" name="acc_mask_other">
                          <Input
                            disabled={accMask !== 'o_other'}
                            name="acc_mask_other"
                            autoComplete={`${Math.random()}`}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={2} className="d-flex alignCenter itemCenter" style={{ marginLeft: '6px' }}>
                        <span>サイズ</span>
                      </Col>
                      <Col span={4}>
                        <Select
                          name="acc_mask_size"
                          className="acc_mask_size"
                          value={accMaskSize}
                          style={{ width: '100%' }}
                          onChange={(value) => setAccMaskSize(value)}
                        >
                          {selectAccMaxSize.map((s: any, i: number) => (
                            <Option key={i} value={s.display_id}>
                              {s.value}
                            </Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={3} style={{ marginLeft: '6px' }}>
                        <Form.Item className="mb-0" name="acc_mask_size_other">
                          <Input disabled={accMaskSize !== 'o_size_other'} name="acc_mask_size_other" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row className="bb-1">
                      <Col span={4} className="itemCenter">
                        <Checkbox
                          checked={checkAccessories(selectAccessories[1].display_id)}
                          onChange={check}
                          value={selectAccessories[1].display_id}
                        >
                          {selectAccessories[1].value}
                        </Checkbox>
                      </Col>
                      <Col span={3} className="alignCenter">
                        <span className="standardBoldFont">加湿器機番</span>
                      </Col>
                      <Col span={4}>
                        <Form.Item className="mb-0" name="humidifier_machine_no">
                          <Input
                            disabled={!checkAccessories(selectAccessories[1].display_id)}
                            name="humidifier_machine_no"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4} style={{ marginLeft: 15 }} className="itemCenter">
                        <Checkbox
                          checked={checkAccessories(selectAccessories[2].display_id)}
                          onChange={check}
                          value={selectAccessories[2].display_id}
                        >
                          {selectAccessories[2].value}
                        </Checkbox>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <div className="form2">
                    <Row>
                      <Col span={11} md={8} xs={5} className="standardBoldFont  alignCenter">
                        次回定期送付月
                      </Col>
                      <Col span={13} md={16} xs={19}>
                        <div className="d-flex alignCenter">
                          <Form.Item name="year" className="mb-0">
                            <DatePicker
                              value={countPrevMonth.year ? moment(countPrevMonth.year) : undefined}
                              style={{ width: '100%' }}
                              placeholder=""
                              suffixIcon={downOutlined}
                              onChange={onChangeYear}
                              picker="year"
                            />
                          </Form.Item>
                          <p className="standardFont m-5px">年</p>
                          <Form.Item className="mb-0" name="month" style={{ width: '25%' }}>
                            <Select
                              name="countPrevMonth"
                              value={countPrevMonth.month}
                              onChange={(e) => setCountPrevMonth((oldState) => ({ ...oldState, month: e }))}
                              options={optionMonths}
                            />
                          </Form.Item>
                          <p className="standardFont m-5px">月</p>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="form2">
                    <Row>
                      <Col span={11} md={8} xs={5} className="standardBoldFont  alignCenter">
                        回収日
                      </Col>
                      <Col span={13} md={16} xs={19}>
                        <div className="d-flex alignCenter">
                          <Form.Item name="collect_month" className="mb-0">
                            <Select
                              style={{ width: '5em' }}
                              name="collect_month"
                              value={collection.month}
                              onChange={(date) => {
                                setCollection((oldState) => ({ ...oldState, month: date }));
                              }}
                              options={optionMonths}
                            />
                          </Form.Item>
                          <p className="standardFont m-5px">月</p>
                          <Form.Item className="mb-0" name="collect_date" style={{ width: '25%' }}>
                            <Select
                              name="collection_date"
                              value={collection.date}
                              options={SelectCollectionDate}
                              onChange={(value) => setCollection((oldState) => ({ ...oldState, date: value }))}
                            />
                          </Form.Item>
                          <p className="standardFont m-5px">日</p>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
              <div className="form2">
                <Row>
                  <Col span={5} className="standardBoldFont alignCenter">
                    設置日
                  </Col>
                  <Col span={19}>
                    <div className="d-flex alignCenter">
                      <p className="standardFont m-5px">設置日</p>
                      <Form.Item className="mb-0" name="month">
                        <DatePicker
                          value={pickDate ? moment(pickDate) : undefined}
                          placeholder=""
                          format="YYYY年MM月DD日"
                          suffixIcon={<CalendarOutlined />}
                          onChange={onChangePickDate}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </div>
              <div className="form2">
                <Row>
                  <Col span={5} className="standardBoldFont d-flex alignCenter">
                    備考
                  </Col>
                  <Col span={19}>
                    <Form.Item name="cpap_remark" className="mb-0">
                      <TextArea name="cpap_remark" rows={6} autoComplete={`${Math.random()}`} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
              <div className="form2">
                <Row>
                  <Col span={5} className="standardBoldFont d-flex alignCenter">
                    受診明細用備考
                  </Col>
                  <Col span={19}>
                    <Form.Item name="remark_for_output" className="mb-0">
                      <Input name="remark_for_output" size={'large'} autoComplete={`${Math.random()}`} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
              <div className="form2">
                <Row>
                  <Col span={5} className="standardBoldFont d-flex alignCenter">
                    利用停止
                  </Col>
                  <Col span={19}>
                    <Form.Item name="suspension_flg" className="mb-0">
                      <Checkbox value={exploitStop} onChange={handleChaneSuspen}>
                        利用停止
                      </Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
            {!isValid && (
              <div className="error">
                <WarningOutlined />
                <p>入力内容に不備がございます。エラーが表示されている項目をご確認ください。</p>
              </div>
            )}
            <div className="submit">
              <SubmitButton className="btnDefault btn">次 へ</SubmitButton>
            </div>
          </Form>
        )}
      />
      <ModalWarningLeavePage />
    </Wrap>
  );
};

export default RegisterSecondStep;
